import * as Location from 'expo-location';
import { Platform } from 'react-native';
import sentry from './sentry';

export const LOCATION_ERROR = {
  PERMISSION_DENIED: 'LOCATION_PERMISSION_DENIED',
  SETTINGS_INVALID: 'LOCATION_SETTINGS_INVALID',
  TIMEOUT: 'LOCATION_TIMEOUT',
  INVALID_COORDINATES: 'LOCATION_INVALID_COORDINATES',
  FOREGROUND_SERVICE_NOT_ALLOWED: 'LOCATION_FOREGROUND_SERVICE_NOT_ALLOWED',
} as const;

export const isValidLocationCoordinates = (
  coords: unknown
): coords is { latitude: number; longitude: number } => {
  if (!coords || typeof coords !== 'object') {
    return false;
  }

  const candidate = coords as { latitude?: unknown; longitude?: unknown };
  if (typeof candidate.latitude !== 'number' || typeof candidate.longitude !== 'number') {
    return false;
  }

  return (
    Number.isFinite(candidate.latitude) &&
    Number.isFinite(candidate.longitude) &&
    candidate.latitude >= -90 &&
    candidate.latitude <= 90 &&
    candidate.longitude >= -180 &&
    candidate.longitude <= 180
  );
};

// Errors caused by user device state (GPS off, signal lost, permission
// revoked) — the UI surfaces these via dialogs that prompt the user to fix
// the setting, or simply by waiting for a better fix. Reporting them to
// Sentry as exceptions just creates noise without giving the team anything
// actionable. Covers the RN location cluster (RN-35, RN-3T, RN-40, RN-3Z).
const USER_STATE_ERRORS: ReadonlyArray<string> = [
  LOCATION_ERROR.SETTINGS_INVALID,
  LOCATION_ERROR.PERMISSION_DENIED,
  // TIMEOUT is a user-condition signal (cold GPS in poor signal / indoor),
  // not a bug. Hit by 13 users across RN-40/RN-3Z in rural Portugal on
  // normal devices — capturing it just creates noise.
  LOCATION_ERROR.TIMEOUT,
];

// True when the (already-normalized) error reflects a recoverable user device
// condition — GPS off, permission revoked, cold/timed-out fix. These are surfaced
// to the user via dialogs/retries, so capturing them in Sentry is pure noise.
// Callers that report their own scope/tags should gate on this before capturing.
export function isUserStateLocationError(normalized: Error): boolean {
  return USER_STATE_ERRORS.includes(normalized.message);
}

export function isLocationUnavailableError(error: unknown): boolean {
  const message = error instanceof Error
    ? error.message
    : typeof error === 'string'
      ? error
      : '';

  const manualFallbackErrors: ReadonlyArray<string> = [
    LOCATION_ERROR.PERMISSION_DENIED,
    LOCATION_ERROR.SETTINGS_INVALID,
    LOCATION_ERROR.TIMEOUT,
    LOCATION_ERROR.INVALID_COORDINATES,
  ];

  return manualFallbackErrors.includes(message);
}

export function reportLocationErrorIfActionable(normalized: Error, platform: string): void {
  if (isUserStateLocationError(normalized)) {
    return;
  }
  sentry.captureException(normalized, (scope) => {
    scope.setTag('location_error_type', normalized.message);
    scope.setExtra('platform', platform);
    return scope;
  });
}

// Walks an Error.cause chain and returns every message joined, lowercased.
// expo-location on iOS wraps native errors with multiple cause layers
// (e.g. "Calling the 'getCurrentPositionAsync' function has failed →
// Caused by: Cannot obtain current location → Caused by: kCLErrorDomain
// error 0"). Matching only against `.message` misses the diagnostic
// signal that lives in the inner causes.
function collectErrorMessages(error: unknown): string {
  const parts: string[] = [];
  let current: unknown = error;
  // Guard against pathological cycles; expo errors are at most 3-4 deep.
  for (let depth = 0; depth < 8 && current; depth++) {
    if (current instanceof Error) {
      if (current.message) parts.push(current.message);
      current = (current as Error & { cause?: unknown }).cause;
    } else if (typeof current === 'object') {
      const candidate = current as { message?: unknown; cause?: unknown };
      if (typeof candidate.message === 'string') parts.push(candidate.message);
      current = candidate.cause;
    } else {
      parts.push(String(current));
      break;
    }
  }
  return parts.join(' \n ').toLowerCase();
}

export function normalizeLocationError(error: unknown): Error {
  const msg = collectErrorMessages(error);
  const err = error as { code?: number };
  if (
    msg.includes("couldn't start the foreground service") ||
    msg.includes('foreground service cannot be started when the application is in the background') ||
    msg.includes('foregroundservicestartnotallowedexception')
  ) {
    return new Error(LOCATION_ERROR.FOREGROUND_SERVICE_NOT_ALLOWED);
  }
  if (
    msg.includes('permission denied') ||
    msg.includes('user denied geolocation') ||
    msg.includes('only secure origins are allowed') ||
    msg.includes('geolocation error') ||
    msg.includes('geolocation api unavailable') ||
    msg.includes('geolocation is not supported') ||
    (typeof err?.code === 'number' && err.code === 1)
  ) {
    return new Error(LOCATION_ERROR.PERMISSION_DENIED);
  }
  if (
    msg.includes('position unavailable') ||
    msg.includes('position_unavailable') ||
    msg.includes('unsatisfied device settings') ||
    msg.includes('location request failed due to unsatisfied') ||
    // Android expo-location when Location Services / GPS is off (RN-35).
    msg.includes('current location is unavailable') ||
    msg.includes('location services are enabled') ||
    // iOS expo-location wrapper + CLError chain when GPS can't lock (RN-3T):
    //   Calling the 'getCurrentPositionAsync' function has failed
    //   → Caused by: Cannot obtain current location
    //   → Caused by: kCLErrorDomain error 0 (kCLErrorLocationUnknown)
    msg.includes('cannot obtain current location') ||
    msg.includes('kclerrordomain') ||
    (typeof err?.code === 'number' && err.code === 2)
  ) {
    return new Error(LOCATION_ERROR.SETTINGS_INVALID);
  }
  if (msg.includes('timed out') || (typeof err?.code === 'number' && err.code === 3)) {
    return new Error(LOCATION_ERROR.TIMEOUT);
  }
  return error instanceof Error ? error : new Error(String(error));
}

const WEB_LOCATION_TIMEOUT_MS = 10000;

const getCurrentPositionWeb = (timeoutMs: number = WEB_LOCATION_TIMEOUT_MS) =>
  new Promise<{ coords: { latitude: number; longitude: number } }>((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new Error('Geolocation API unavailable'));
      return;
    }

    let settled = false;
    const done = (handler: () => void) => {
      if (settled) return;
      settled = true;
      handler();
    };

    const timeoutId = setTimeout(() => {
      done(() => reject(new Error('Location request timed out')));
    }, timeoutMs);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        done(() => {
          clearTimeout(timeoutId);
          resolve(position as { coords: { latitude: number; longitude: number } });
        });
      },
      (error) => {
        done(() => {
          clearTimeout(timeoutId);
          // GeolocationPositionError is not an Error instance on most browsers.
          // Wrap it to prevent "Non-Error thrown: [empty object]" in Sentry.
          reject(normalizeLocationError(error));
        });
      },
      {
        enableHighAccuracy: true,
        timeout: timeoutMs,
        maximumAge: 0,
      }
    );
  });

export const getLocationString = async (): Promise<string> => {
  try {
    if (Platform.OS === 'web') {
      const result = await getCurrentPositionWeb(WEB_LOCATION_TIMEOUT_MS);
      if (!result || !isValidLocationCoordinates(result.coords)) {
        throw new Error(LOCATION_ERROR.INVALID_COORDINATES);
      }
      return `POINT(${result.coords.longitude} ${result.coords.latitude})`;
    }

    // Check current permission status first to avoid hanging
    let { status } = await Location.getForegroundPermissionsAsync();

    if (status !== 'granted') {
      const result = await Location.requestForegroundPermissionsAsync();
      status = result.status;
    }

    if (status !== 'granted') {
      throw new Error('Permission denied');
    }

    // Add timeout to prevent infinite loading
    const locationPromise = Location.getCurrentPositionAsync({
      accuracy: Location.Accuracy.Balanced,
    });

    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Location request timed out')), WEB_LOCATION_TIMEOUT_MS)
    );

    const result = await Promise.race([locationPromise, timeoutPromise]);
    const coords = (result as { coords?: unknown } | null)?.coords;
    if (!isValidLocationCoordinates(coords)) {
      throw new Error(LOCATION_ERROR.INVALID_COORDINATES);
    }
    return `POINT(${coords.longitude} ${coords.latitude})`;
  } catch (error: unknown) {
    const normalized = normalizeLocationError(error);
    console.error('Error getting location:', normalized);
    reportLocationErrorIfActionable(normalized, Platform.OS);
    throw normalized;
  }
};
